import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AutoAwesomeMotionRounded as ContentIcon,
  RefreshRounded as RefreshIcon,
  WifiOffRounded as OfflineIcon,
  WifiRounded as OnlineIcon,
} from "@mui/icons-material";
import { PageHeader, EmptyState } from "../common";
import ContentTemplateLibrary from "./ContentTemplateLibrary";
import ContentReviewCard, {commandWaitingForChild} from "./ContentReviewCard";
import ContentRejectConfirm from "./ContentRejectConfirm";
import ContentMarkPostedDialog from "./ContentMarkPostedDialog";
import { beatRepetition } from "./sceneBeats";
import ReviewSheetVideo from "./ReviewSheetVideo";
import ManualFootageControl from "./ManualFootageControl";
import {clientStore} from "../../lib/clientStorage";
import {clearPendingFootage,readPendingFootage,savePendingFootage,manualFootageEnabled,getManualFootageOwner,manualFootageOwnerCurrent,type ManualFootageOwner,type PendingFootageRequest} from "../../services/manualFootageService";
import ShotCopyDialog from "../socials/reels/ShotCopyDialog";
import { shotCopyTarget, type ShotCopyTarget } from "../../lib/shotCopyTarget";
import RedditAnswerDesk from "./RedditAnswerDesk";
import InstagramActionDesk from "./InstagramActionDesk";
import { ApiError } from "../../lib/api";
import {
  contentReviewService,
  type ContentCommandTruth,
  type ContentCommandState,
  type ContentGridVerdict,
  type ContentMarkPostedInput,
  type ContentPostedSummary,
  type ContentRevisionVerdict,
  type ContentReviewItem,
  type ContentReviewQueue,
  type ContentReviewTab,
  type ContentVerdictResponse,
  type ContentWorkerHealth,
  REVIEW_NOTE_MAX,
} from "../../services/contentReviewService";
import { TOUCH_TARGET } from "../../theme/theme";
import { useCoordinatedRefresh } from "../../hooks/useCoordinatedRefresh";
import { beginDecision } from "../../lib/decisionIdentity";

/**
 * PKT-0527 — Grant's three tabs, in his words:
 *   "Approved (with download button), Review (with check/x icon for approve /
 *    reject and actual review button to submit notes to queue for re-render),
 *    Rejected (the really bad ones to learn from)"
 *
 * A tab is a VIEW over the statuses the box already writes (migration 053:86-87);
 * the server resolves the span, so a tab is one request, not six.
 */
const TABS: Array<{
  id: ContentReviewTab;
  label: string;
  emptyTitle: string;
  emptyBody: string;
}> = [
  {
    id: "review",
    label: "Review",
    emptyTitle: "Nothing waiting",
    emptyBody:
      "New reels land here when the box publishes. Approve one and it moves to Approved; anything that fails QA moves to Rejected.",
  },
  {
    id: "approved",
    label: "Approved",
    emptyTitle: "No approved reels yet",
    emptyBody:
      "Approve a reel in Review and it lands here with a download button, ready to post. An empty Approved tab is the normal first day — not an error.",
  },
  {
    id: "rejected",
    label: "Rejected",
    emptyTitle: "Nothing rejected yet",
    emptyBody:
      "This is a corpus, not a bin — nothing here is ever deleted. Reels that fail QA or arrive corrupt are kept so you can see the pattern.",
  },
];

const SHEET_PAPER = {
  maxWidth: 560,
  mx: "auto",
  borderRadius: "24px 24px 0 0",
  p: { xs: 2, sm: 3 },
  pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
} as const;

/**
 * 🔴 THE RE-RENDER DIALS — AND WHY THERE IS NO "APPROVE" AMONG THEM.
 *
 * Measured 2026-08-15: the only verdict ever written to this system carried
 * verdict "P" and the note "Text on screen needs work". P is
 * `{ operations: [], max_attempts: 0, stop_code: "R-APPROVED" }`
 * (revisionEngine.ts:36) — approved-and-stop, zero operations. A correction
 * filed as an approval is discarded by the engine, silently, and that is what
 * happened to the one real note Grant ever wrote here.
 *
 * ⇒ SO THE INTENTS ARE STRUCTURALLY SEPARATE IN THIS UI. Approve is a single
 *   tap that carries NO note and opens NO sheet; SLICE B's Reject ("H") is the
 *   same, for the same reason — it is the other zero-operation verdict. Notes
 *   exist only in this sheet, only alongside a revision dial, and the submit
 *   stays disabled until a dial is chosen. There is no reachable path that
 *   attaches words to a terminal verdict — not a warning about one, an absence
 *   of one.
 *
 * 🔴 THE LIST BELOW IS TYPED `ContentRevisionVerdict`, WHICH IS THE ALLOWLIST
 *   MINUS THE TERMINAL SET. That is what stops "H" from being added here as a
 *   fourth dial once it exists in `ContentGridVerdict`. The type does the
 *   refusing; the comment only explains it.
 */
/*
 * PKT-0792 · EXPORTED, NOT MOVED — and the distinction is load-bearing.
 *
 * The Socials review screen needs these same dials, and two copies of a verdict
 * list is exactly the drift this constant already has a backend test guarding
 * against. But that guard —
 * `backend/tests/test_content_verdict_allowlist_contract.py:407` — locates this
 * block by FILE PATH plus the regex /const REVISION_DIALS[^=]*=\s*\[(.*?)\n\];/.
 * Relocating it to a shared module would leave the guard matching nothing.
 *
 * `export` keeps the path and the regex intact (it matches `const REVISION_DIALS`
 * wherever it appears) while giving the new screen the SAME array, so the two
 * surfaces cannot offer different dials.
 */
export const REVISION_DIALS: Array<{
  verdict: ContentRevisionVerdict;
  label: string;
  /** What the engine will DO. Only true when the package can be revised. */
  help: string;
  /**
   * PKT-0427 — what the dial NAMES, for a reel the box cannot re-render. Saying
   * "Rewrites the hook copy… Up to 3 tries" beside a warning that re-rendering
   * is unavailable would contradict it in the same breath, so the note-only
   * copy describes the SUBJECT of the note and promises no work.
   */
  helpNoteOnly: string;
}> = [
  {
    verdict: "K-HOOK",
    label: "The opening",
    help: "Rewrites the hook copy and its pacing. Up to 3 tries.",
    helpNoteOnly: "The hook copy and its pacing.",
  },
  {
    verdict: "K-COPY",
    label: "The words",
    help: "Rewrites the on-screen copy and captions. Up to 3 tries.",
    helpNoteOnly: "The on-screen copy and captions.",
  },
  {
    verdict: "K-LAYOUT",
    label: "The layout",
    help: "Fixes framing, hierarchy, and where things sit. Up to 3 tries.",
    helpNoteOnly: "Framing, hierarchy, and where things sit.",
  },
  /*
   * Ch72 · MIGRATION 088 — TWO DIALS THE BOX HAS ALWAYS HAD AND LN PRO NEVER
   * OFFERED.
   *
   * These are NOT new capability on the box: `revisionEngine.ts:20` has carried
   * all eight verdicts since the revision policy shipped. LN Pro accepted five.
   * 078 excluded these two deliberately — "a separate capability question" —
   * and Grant answered that question on 2026-08-29.
   *
   * 🔴 THEY LAND IN THE SAME COMMIT AS THE ALLOWLIST, AND THAT IS ENFORCED.
   * `test_revision_dials_are_the_full_allowlist_minus_the_terminal_verdicts`
   * asserts REVISION_DIALS == allowlist − terminals, so the half-widened state
   * is not reachable. Its own message says why: "a revision dial the backend
   * accepts but the sheet never offers is a dial the reviewer cannot reach; a
   * dial the sheet offers but the backend rejects is a button that fails on
   * click."
   *
   * ⚠️ K-FOOT IS STILL ABSENT, AND DELIBERATELY. It is the one the engine
   * steers to when copy is claim-locked — and `reviewCockpit.ts:730-736`
   * requires failed_scene_id AND failed_clip_id AND q_codes UNCONDITIONALLY.
   * LN Pro can send none of them, so a K-FOOT dial would return
   * R-NEEDS-TARGET:K-FOOT every single time. It waits on PKT-0602's anchor
   * contract. K-PACE and K-MIX are safe because their target check
   * (reviewCockpit.ts:762) fires only when q_codes includes "Q-SYNC", and LN
   * Pro always sends q_codes = [].
   */
  {
    verdict: "K-PACE",
    label: "The pacing",
    help: "Retimes the cuts — how long each shot holds. Up to 3 tries.",
    helpNoteOnly: "How long each shot holds.",
  },
  {
    verdict: "K-MIX",
    label: "The sound",
    /*
     * "The sound", not "the mix": every other label here is the words Grant
     * would use out loud, and the sheet asks him to "say it in your own words".
     * K-MIX covers levels and how the audio sits under the cut.
     */
    help: "Rebalances the audio — levels and how it sits under the cut. Up to 3 tries.",
    helpNoteOnly: "Audio levels and how the sound sits under the cut.",
  },
];

/*
 * 🔴 K-FOOT LIVES HERE AND *NOT* IN `REVISION_DIALS`, AND THAT IS ENFORCED.
 *
 * `test_revision_dials_are_the_full_allowlist_minus_the_terminal_verdicts`
 * asserts REVISION_DIALS == the accepted allowlist minus the terminals. K-FOOT
 * is deliberately NOT in the allowlist (migration 088 excludes it, and the route
 * still 422s it), so putting it in this list would red that test immediately —
 * correctly, because it would mean the sheet was offering a verdict the backend
 * rejects. A chip the reviewer can SEE but cannot SEND is a different thing from
 * a dial, so it gets a different constant.
 *
 * It leaves this constant and joins REVISION_DIALS when PKT-0602 lands the
 * anchor contract — scene list, per-scene license flags, and the anchor fields
 * on both ReviewVerdictInput and the hashed payload.
 */
export const FENCED_DIAL = {
  label: "The footage",
  fencedBecause:
    "Free-form footage directions aren’t supported here.",
} as const;

const createRequestId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `content-${Date.now()}-${Math.random().toString(16).slice(2)}`;

type ContentSource = "videos" | "reddit" | "instagram" | "templates";
const readSource = (): ContentSource => {
  const value = new URLSearchParams(window.location.search).get("tab");
  return value === "reddit" || value === "instagram" || value === "templates" ? value : "videos";
};

const ContentPageView: React.FC<{owner:ManualFootageOwner|null}> = ({owner}) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const searchParams = new URLSearchParams(window.location.search);
  const [source, setSourceState] = React.useState<ContentSource>(readSource);
  const setSource = (next: ContentSource) => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", next);
    url.searchParams.delete("definition");
    if (url.href !== window.location.href) window.history.pushState(window.history.state, "", url);
    setSourceState(next);
  };
  React.useEffect(() => {
    const sync = () => setSourceState(readSource());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);
  const [tab, setTab] = React.useState<ContentReviewTab>("review");
  const [queue, setQueue] = React.useState<ContentReviewQueue | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [sendBackTarget, setSendBackTarget] = React.useState<ContentReviewItem | null>(null);
  // The ✗'s confirm target. Note there is deliberately no companion note
  // state: the confirm carries one bit, yes or no. See ContentRejectConfirm.
  const [selectedBeat, setSelectedBeat] = React.useState<ShotCopyTarget | null>(null);
  const [editingBeat, setEditingBeat] = React.useState<ShotCopyTarget | null>(null);
  const [footageLocked, setFootageLocked] = React.useState(false);
  const [recoveringFootage, setRecoveringFootage] = React.useState<PendingFootageRequest|null>(null);
  const [footageSeek, setFootageSeek] = React.useState<{offsetSec:number;sequence:number}|undefined>();
  const [pendingFootage, setPendingFootage] = React.useState<PendingFootageRequest[]>(() => {
    try {return owner ? readPendingFootage(owner) : [];} catch {return [];}
  });
  const [footageJournalError, setFootageJournalError] = React.useState(false);
  const [rejectTarget, setRejectTarget] = React.useState<ContentReviewItem | null>(null);
  const [dial, setDial] = React.useState<ContentRevisionVerdict | null>(null);
  const [note, setNote] = React.useState("");
  const [notice, setNotice] = React.useState<string | null>(null);
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  const [workerHealth, setWorkerHealth] = React.useState<ContentWorkerHealth | null>(null);
  // PKT-0612 — the "Mark as posted" sheet's target, and the ledger summary
  // (`content.posted.v1`). `null` summary = not read yet; a summary whose
  // status is "unknown" renders as unknown, never as 0.
  const [postedTarget, setPostedTarget] = React.useState<ContentReviewItem | null>(null);
  const [postedSummary, setPostedSummary] = React.useState<ContentPostedSummary | null>(null);
  const [recordingPostId, setRecordingPostId] = React.useState<string | null>(null);
  const [unknownById, setUnknownById] = React.useState<Set<string>>(new Set());
  // The note is part of decision identity for retries: a retry must resend the
  // SAME note, never a second one.
  const pendingDecisions = React.useRef(
    new Map<string, { requestId: string; verdict: ContentGridVerdict; note: string | null }>()
  );
  // Accepted exact-copy/manual-footage identities are UI truth, never generic-verdict retries.
  const acceptedCopies = React.useRef(new Map<string, {command: ContentCommandTruth; fingerprint: string; previousRequestId: string | null}>());
  const loadEpochRef = React.useRef(0);

  const tabConfig = TABS.find((entry) => entry.id === tab) ?? TABS[0];

  /**
   * PKT-0427 — the open review sheet is aimed at a reel the box cannot rebuild.
   * The note still lands (migration 055 stores `review_note`; migration 085
   * leases it to the box), so the sheet stays open for business and only its
   * PROMISES change.
   */
  const sheetRevisionUnavailable = Boolean(
    sendBackTarget && !sendBackTarget.revision_capable
  );

  const closeSheets = React.useCallback(() => {
    setSendBackTarget(null);
    setSelectedBeat(null);
    setEditingBeat(null);
    setFootageLocked(false);
    setRecoveringFootage(null);
    setFootageSeek(undefined);
    setRejectTarget(null);
    setPostedTarget(null);
    setDial(null);
    setNote("");
  }, []);

  React.useEffect(() => {
    // Navigation dismisses UI only. Pending request identities remain journaled.
    closeSheets();
  }, [source, closeSheets]);

  /**
   * PKT-0612 — read the posted ledger's summary. The reader never throws to
   * the page: a failed read becomes an honest "unknown" envelope (status
   * unknown, value null), which the Approved header renders as unknown.
   */
  const loadPostedSummary = React.useCallback(async () => {
    try {
      setPostedSummary(await contentReviewService.postedSummary());
    } catch {
      setPostedSummary({
        value: null,
        status: "unknown",
        definition_id: "content.posted.v1",
        computed_at: new Date().toISOString(),
        last_posted_at: null,
        per_platform: null,
        note: "summary request failed",
      });
    }
  }, []);

  // TRUE full-bleed reel: the feed is a fixed layer under the translucent app
  // bar and the floating nav — the video IS the screen; chrome floats over it.
  // 100dvh tracks the dynamic mobile viewport, so the reel can never mis-size
  // when the browser bar collapses/expands.
  const feedRef = React.useRef<HTMLDivElement | null>(null);
  // Lock the page behind the fixed reel so iOS rubber-banding can't fight the
  // feed's own snap scroll.
  React.useEffect(() => {
    if (!mobile || source !== "videos") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobile, source]);
  const feedHeight = "100dvh";

  // Scroll position decides which reel plays — one at a time, snap-aligned.
  const [activeIndex, setActiveIndex] = React.useState(0);

  // After a decision lands, carry the reviewer to the next reel. Deciding is
  // the whole loop; making them scroll back to where they were is the tax.
  const advanceToNextReel = React.useCallback(() => {
    const node = feedRef.current;
    if (!mobile || !node) return;
    const card = node.firstElementChild as HTMLElement | null;
    const cardHeight = card?.getBoundingClientRect().height ?? 0;
    if (cardHeight <= 0) return;
    const nextIndex = Math.round(node.scrollTop / cardHeight) + 1;
    const nextTop = nextIndex * cardHeight;
    if (nextTop > node.scrollHeight - cardHeight) return;
    // Name the active reel directly rather than waiting for a scroll event:
    // programmatic scrolls do not reliably emit one (verified in the preview
    // browser), and the next reel must start playing, not wait for an event.
    setActiveIndex(nextIndex);
    node.scrollTo({ top: nextTop, behavior: "smooth" });
    // Some runtimes ignore smooth scrolling outright (verified: the preview
    // browser moves 0px). Land it anyway rather than silently stranding the
    // reviewer on the reel they just decided.
    window.setTimeout(() => {
      const current = feedRef.current;
      if (current && Math.abs(current.scrollTop - nextTop) > 1) {
        current.scrollTop = nextTop;
      }
    }, 350);
  }, [mobile]);
  React.useEffect(() => {
    const node = feedRef.current;
    if (!mobile || !node) return;
    let frame = 0;
    const sync = () => {
      frame = 0;
      const card = node.firstElementChild as HTMLElement | null;
      const cardHeight = card?.getBoundingClientRect().height ?? 0;
      if (cardHeight <= 0) return;
      setActiveIndex(Math.round(node.scrollTop / cardHeight));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(sync);
    };
    sync();
    node.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      node.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
    // Deliberately NOT keyed on `queue`: a decision updates the queue, and
    // re-running the initial sync mid-advance would re-measure a scroll
    // position that has not moved yet and clobber the reel we just advanced to.
  }, [mobile, loading, source]);

  const updateCommand = React.useCallback((itemId: string, command: ContentCommandTruth) => {
    setQueue((current) => current ? {
      ...current,
      items: current.items.map((item) => item.id === itemId ? { ...item, command } : item),
    } : current);
  }, []);

  const load = React.useCallback(async (silent = false) => {
    const requestEpoch = ++loadEpochRef.current;
    const isCurrentRequest = () => requestEpoch === loadEpochRef.current && (!owner || manualFootageOwnerCurrent(owner));
    if (!silent) {
      setLoading(true);
      setQueue(null);
      setError(null);
    }
    try {
      const nextQueue = await contentReviewService.list(tab);
      if (!isCurrentRequest()) return;
      // A saved unresolved POST must not become a second command after refresh.
      // Only matching/superseding server truth clears its bounded tab journal.
      try {
        for (const saved of owner ? readPendingFootage(owner) : []) {
          const item = nextQueue.items.find(row => row.id === saved.packageId);
          if (!item || !owner) continue;
          const matching = item.command?.request_id === saved.request.request_id;
          const superseded = (item.fingerprint && item.fingerprint !== saved.request.fingerprint)
            || (item.command && !matching && item.command.request_id !== saved.previousRequestId)
            || ["P", "H"].includes(item.verdict);
          if (superseded || (matching && item.command && (
            ["refused", "exhausted", "conflict"].includes(item.command.state)
            || (item.command.state === "applied" && item.command.child_ready === true)))) {
            clearPendingFootage(saved.packageId, saved.request.request_id, owner);
          } else if (matching && item.command && ["queued", "leased", "retry", "applied"].includes(item.command.state)) {
            // Applied only acknowledges rendering. Publication can still fail;
            // retain this immutable identity and never offer another render.
            savePendingFootage({...saved, confirmedState: item.command.state as PendingFootageRequest["confirmedState"]}, owner);
          }
        }
        setPendingFootage(owner ? readPendingFootage(owner) : []);
        setFootageJournalError(false);
      } catch {setFootageJournalError(true);}
      // Keep a confirmed copy/footage request visible through a failed/stale mirror refresh.
      // Never turn its exact payload into a generic verdict Retry.
      const reconciledItems = nextQueue.items.map((item) => {
        const accepted = acceptedCopies.current.get(item.id);
        if (!accepted) return item;
        if (item.command?.request_id === accepted.command.request_id
          || item.fingerprint !== accepted.fingerprint
          || (item.command && item.command.request_id !== accepted.previousRequestId)
          || ["P", "H"].includes(item.verdict)) {
          acceptedCopies.current.delete(item.id);
          return item;
        }
        return { ...item, command: accepted.command };
      });
      setQueue({...nextQueue, items: reconciledItems});
      const confirmedIds = new Set<string>();
      for (const item of nextQueue.items) {
        const pending = pendingDecisions.current.get(item.id);
        if (
          item.command
          && pending?.requestId === item.command.request_id
          && pending.verdict === item.command.verdict
        ) {
          confirmedIds.add(item.id);
          if (["applied", "exhausted"].includes(item.command.state)) {
            pendingDecisions.current.delete(item.id);
          }
        }
      }
      if (confirmedIds.size > 0) {
        setUnknownById((current) => {
          const next = new Set(current);
          confirmedIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    } catch {
      if (!silent && isCurrentRequest()) {
        setError("The private review mirror is unavailable. Your review queue was not changed.");
      }
    } finally {
      if (!silent && isCurrentRequest()) setLoading(false);
    }
    if (!isCurrentRequest()) return;
    try {
      const health = await contentReviewService.health();
      if (isCurrentRequest()) setWorkerHealth(health);
    } catch {
      // A transient refresh failure must not replace a known last-good worker
      // snapshot with fabricated unknowns. Cold failure still renders honest
      // unknown health until a later resume/poll succeeds.
      if (isCurrentRequest()) setWorkerHealth((current) => current ?? ({
          state: "unknown",
          last_heartbeat_at: null,
          oldest_pending_seconds: null,
          exhausted_commands: 0,
        }));
    }
  }, [tab, owner]);

  const trackFootage = (saved: PendingFootageRequest) => {
    if(!manualFootageOwnerCurrent(owner))return;
    // A list request begun before this write cannot settle its identity.
    ++loadEpochRef.current;
    setPendingFootage(previous => [...previous.filter(row => row.packageId !== saved.packageId), saved]);
  };

  const confirmFootage = (saved: PendingFootageRequest, state: ContentCommandState) => {
    if(!manualFootageOwnerCurrent(owner))return;
    const command: ContentCommandTruth = {
      request_id: saved.request.request_id, verdict: "K-FOOT", state, applied: state === "applied",
    };
    acceptedCopies.current.set(saved.packageId, {
      command, fingerprint: saved.request.fingerprint, previousRequestId: saved.previousRequestId,
    });
    updateCommand(saved.packageId, command);
    if (!["refused", "exhausted", "conflict"].includes(state)) {
      try {
        savePendingFootage({...saved, confirmedState:state as PendingFootageRequest["confirmedState"]}, owner);
        setPendingFootage(readPendingFootage(owner));
      } catch {setFootageJournalError(true);}
    } else {
      try {
        if(owner)clearPendingFootage(saved.packageId, saved.request.request_id, owner);
        setPendingFootage(owner ? readPendingFootage(owner) : []);
      } catch {setFootageJournalError(true);}
    }
    closeSheets();
    setNotice(["refused", "exhausted", "conflict"].includes(state)
      ? "The footage request did not complete. Review its status before trying another change."
      : state === "applied" ? "Footage request applied. Waiting for the new video to reach Review before approval."
      : "Alternate clip requested. The new video must return to Review before approval.");
    void load(true);
  };

  React.useLayoutEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (tab === "approved") void loadPostedSummary();
  }, [tab, loadPostedSummary]);

  const needsTruthRefresh = Boolean(
    unknownById.size > 0
    || pendingFootage.length > 0
    || queue?.items.some((item) => item.command
      && (["queued", "leased", "retry"].includes(item.command.state) || commandWaitingForChild(item.command)))
  );

  const refreshTruth = React.useCallback(async () => {
    await load(true);
  }, [load]);
  useCoordinatedRefresh(refreshTruth, {
    enabled: needsTruthRefresh,
    pollMs: 10_000,
    requestOnMount: false,
  });

  const decide = React.useCallback(
    async (item: ContentReviewItem, verdict: ContentGridVerdict, reviewNote: string | null) => {
      /*
       * PKT-0792 PR 2 — these four lines moved to lib/decisionIdentity.ts and
       * this now CALLS it. Behaviour is unchanged; the point is that the Socials
       * tab needs the identical rule the moment its verdict button is wired, and
       * two copies of an idempotency rule drift. The copy that drifts is the one
       * that files a second decision on Grant's content.
       *
       * Still true here, and now asserted by that lib's tests rather than only
       * by this comment: a retry reuses the SAME request id and REPLAYS the
       * original note; a different verdict while one is pending is refused.
       */
      if (pendingFootage.some(saved => saved.packageId === item.id) || (manualFootageEnabled() && footageJournalError)) return;
      const pending = pendingDecisions.current.get(item.id);
      const begun = beginDecision(pending, verdict, reviewNote, createRequestId);
      if (!begun.decision) return;
      const requestId = begun.decision.requestId;
      const pendingNote = begun.decision.note;
      /*
       * PKT-0427 — a K-* request on a package the box cannot revise will retry
       * to its 20-attempt cap (053:527) and land `exhausted`. The snackbar must
       * not announce a re-render that is not coming, nor send Grant to an Admin
       * with nothing to do. `startsWith("K-")` is false for both terminals, so
       * this branch can never relabel an approve or a reject.
       */
      const noteOnlyOutcome = verdict.startsWith("K-") && !item.revision_capable;
      pendingDecisions.current.set(item.id, { requestId, verdict, note: pendingNote });
      setBusyId(item.id);
      setError(null);
      try {
        const response = await contentReviewService.verdict(
          item.id,
          verdict,
          requestId,
          pendingNote
        );
        updateCommand(item.id, response);
        setUnknownById((current) => {
          const next = new Set(current);
          next.delete(item.id);
          return next;
        });
        closeSheets();
        // Only a decision the engine accepted should move the reviewer on.
        if (["applied", "queued", "leased", "retry"].includes(response.state)) {
          advanceToNextReel();
        }
        if (response.state === "applied") {
          pendingDecisions.current.delete(item.id);
          /*
           * PKT-0533 — this used to read "Applied by the Content engine", and
           * for a terminal verdict on a hand-seeded card that is now FALSE: the
           * Content engine never saw it. LN Pro applied it itself, because the
           * box has no package on disk to act on (migration 081).
           *
           * The response cannot tell the two apart — `applied` is `applied`
           * whichever half recorded it — and inventing a flag to split the
           * copy would put a claim about WHICH SYSTEM ACTED into a snackbar
           * that nobody reads for that. So the copy stops naming an actor it
           * cannot verify and states the outcome, which is true on both paths
           * and is the thing the reviewer actually wants confirmed.
           */
          setNotice("Decision recorded");
          await load();
        } else if (response.state === "conflict") {
          pendingDecisions.current.delete(item.id);
          setNotice("Decision conflict — nothing changed");
        } else if (response.state === "exhausted") {
          pendingDecisions.current.delete(item.id);
          setNotice(noteOnlyOutcome
            ? "Saved — the engine couldn’t re-render this reel"
            : "Couldn’t apply — Admin review needed");
        } else {
          setNotice(noteOnlyOutcome
            ? "Saved — re-render unavailable for this reel"
            : "Queued — waiting for Content engine");
        }
      } catch (caught) {
        // Close the sheet even when the decision fails: recovery (Retry / the
        // conflict state) lives on the card, and a sheet left open hides it.
        closeSheets();
        const conflict = caught instanceof ApiError
          && caught.status === 409
          && caught.body
          && typeof caught.body === "object"
          ? caught.body as Partial<ContentVerdictResponse>
          : null;
        if (
          conflict?.state === "conflict"
          && conflict.request_id === requestId
          && conflict.verdict === verdict
        ) {
          updateCommand(item.id, {
            request_id: requestId,
            verdict,
            state: "conflict",
            applied: false,
          });
          pendingDecisions.current.delete(item.id);
          setUnknownById((current) => {
            const next = new Set(current);
            next.delete(item.id);
            return next;
          });
          setNotice("Decision conflict — nothing changed");
        } else {
          setUnknownById((current) => new Set(current).add(item.id));
          setError("Decision status is unknown. Retry safely with the same request.");
        }
      } finally {
        setBusyId(null);
      }
    },
    [advanceToNextReel, closeSheets, load, updateCommand, pendingFootage, footageJournalError]
  );

  /**
   * APPROVE — one tap, and it takes no note. This signature is the guard: it
   * has no parameter a caller could pass words through, so a correction cannot
   * be filed as an approval even by mistake in a later edit.
   */
  const approve = React.useCallback(
    (item: ContentReviewItem) => { void decide(item, "P", null); },
    [decide]
  );

  /**
   * REJECT — SLICE B, verdict "H". TWO STEPS, and no note in either of them.
   *
   * Step 1 is `setRejectTarget`, wired straight to the card: the ✗ opens a
   * confirm and submits nothing. Grant ruled for the confirm — "confirm step -
   * dont want to lose one by accident" — because a rejected package lands in
   * `parked`, which is absent from VERDICT_ELIGIBLE_STATUSES, so a mis-tap is
   * unrecoverable from this UI. Step 2 is this handler.
   *
   * 🔴 THE CONFIRM IS NOT A PLACE TO PUT A NOTE, and that is the whole reason
   * ContentRejectConfirm is its own component rather than another Drawer in
   * this file. A confirmation sheet is exactly the affordance a later edit
   * would grow a "say why" box onto. An earlier draft of this slice shipped
   * one, captioned "say why, so the engine learns what a bad one looks like" —
   * false in the same way the 2026-07-25 approval was: H is
   * `{ operations: [], max_attempts: 0, stop_code: "R-HOLD" }`
   * (revisionEngine.ts:37), so the engine runs nothing on it and learns nothing
   * from the words. Attaching a note to a terminal verdict IS the defect
   * PKT-0527 closed, whichever terminal verdict it is and whichever surface it
   * is bolted onto. The note-carrying path below is typed
   * `ContentRevisionVerdict`, which excludes "H" by construction.
   */
  const confirmReject = React.useCallback(
    (item: ContentReviewItem) => { void decide(item, "H", null); },
    [decide]
  );

  /** SEND BACK — a revision verdict, and the only path that carries a note. */
  const sendBack = React.useCallback(
    (item: ContentReviewItem, verdict: ContentRevisionVerdict, reviewNote: string) => {
      void decide(item, verdict, reviewNote);
    },
    [decide]
  );

  const download = React.useCallback(async (item: ContentReviewItem) => {
    setDownloadingId(item.id);
    try {
      await contentReviewService.download(item);
      setNotice("Saved to your downloads");
    } catch {
      setNotice("Couldn’t save that reel — try again");
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const retry = React.useCallback((item: ContentReviewItem) => {
    const pending = pendingDecisions.current.get(item.id);
    if (pending) void decide(item, pending.verdict, pending.note);
  }, [decide]);

  /**
   * PKT-0612 — RECORD a manual post. Two taps from the Approved card: open
   * the sheet, "Record post". posted_by is the session user on the server;
   * nothing here can name one. A replay (same reel, same platform) is
   * reported as such, not as a second post. The ledger is then re-read so the
   * header moves because the ledger moved, not because we assumed.
   */
  const recordPost = React.useCallback(
    async (item: ContentReviewItem, input: ContentMarkPostedInput) => {
      setRecordingPostId(item.id);
      try {
        const record = await contentReviewService.markPosted(item.id, input);
        setNotice(record.replayed ? "Already recorded — nothing changed" : "Post recorded");
        closeSheets();
        await Promise.all([loadPostedSummary(), load(true)]);
      } catch (caught) {
        const status = caught instanceof ApiError ? caught.status : 0;
        setNotice(
          status === 409
            ? "Only an approved reel can be recorded as posted"
            : status === 503
              ? "The posted ledger isn’t available — nothing was recorded"
              : "Couldn’t record that post — try again"
        );
      } finally {
        setRecordingPostId(null);
      }
    },
    [closeSheets, load, loadPostedSummary]
  );

  /** "Posted N · last <date>" — or an honest unknown. Never a fake zero. */
  const postedHeadline = postedSummary === null
    ? "Posted — reading ledger…"
    : postedSummary.status !== "ok" || postedSummary.value === null
      ? "Posted — unknown (ledger unreadable)"
      : postedSummary.value === 0
        ? "No posts recorded yet"
        : `Posted ${postedSummary.value} · last ${
            postedSummary.last_posted_at
              ? new Date(postedSummary.last_posted_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
              : "unknown"
          }`;

  const workerLabel = workerHealth?.state === "online"
    ? "Engine online"
    : workerHealth?.state === "stale"
      ? "Engine delayed"
      : workerHealth?.state === "offline"
        ? "Engine offline · decisions queue safely"
        : "Engine status unknown";
  const workerNeedsAttention = Boolean(workerHealth?.exhausted_commands);
  const healthWarning = workerNeedsAttention
    ? `${workerHealth?.exhausted_commands} need Admin`
    : error
      ? "Mirror unavailable"
      : workerHealth?.state === "offline"
        ? "Engine offline"
        : null;


  if (source === "templates") {
    return <Box sx={{ pb: 4 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Chip label="Videos" onClick={() => setSource("videos")} />
        <Chip label="Reddit" onClick={() => setSource("reddit")} />
        <Chip label="Instagram" onClick={() => setSource("instagram")} />
        <Chip label="Templates" color="primary" />
      </Stack>
      <ContentTemplateLibrary />
    </Box>;
  }

  if (source === "reddit") {
    return (
      <Box sx={{ pb: 4 }}>
        <PageHeader icon={<ContentIcon />} title="Content" titleVariant="h5" subtitle="Helpful Reddit replies, copied for a human to post." />
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip label="Videos" onClick={() => setSource("videos")} />
          <Chip label="Reddit" color="primary" />
          <Chip label="Instagram" data-testid="content-source-instagram" onClick={() => setSource("instagram")} />
          <Chip label="Templates" onClick={() => setSource("templates")} />
        </Stack>
        <RedditAnswerDesk initialLead={searchParams.get("lead")} />
      </Box>
    );
  }

  if (source === "instagram") {
    return (
      <Box sx={{ pb: 4 }}>
        <PageHeader
          icon={<ContentIcon />}
          title="Content"
          titleVariant="h5"
          subtitle="Posts worth engaging with, opened and acted on by a human."
        />
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip label="Videos" onClick={() => setSource("videos")} />
          <Chip label="Reddit" onClick={() => setSource("reddit")} />
          <Chip label="Instagram" color="primary" />
          <Chip label="Templates" onClick={() => setSource("templates")} />
        </Stack>
        <InstagramActionDesk />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 0, sm: 4 }, position: mobile ? "relative" : undefined }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip label="Videos" color="primary" />
        <Chip label="Reddit" onClick={() => setSource("reddit")} />
        <Chip label="Instagram" data-testid="content-source-instagram" onClick={() => setSource("instagram")} />
          <Chip label="Templates" onClick={() => setSource("templates")} sx={{ display: mobile ? "none" : undefined }} />
      </Stack>
      {!mobile && <PageHeader
        icon={<ContentIcon />}
        title="Content"
        titleVariant="h5"
        subtitle="Approve what you'd post. Send the rest back with a note. Keep the bad ones to learn from."
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={queue ? <OnlineIcon /> : error ? <OfflineIcon /> : <CircularProgress size={16} color="inherit" />}
              label={queue ? "Mirror ready" : error ? "Mirror unavailable" : "Connecting"}
              color={queue ? "success" : "default"}
              variant={queue ? "filled" : "outlined"}
            />
            <Chip
              icon={workerHealth?.state === "online" ? <OnlineIcon /> : <OfflineIcon />}
              label={workerNeedsAttention
                ? `${workerHealth?.exhausted_commands} decision${workerHealth?.exhausted_commands === 1 ? "" : "s"} need Admin review`
                : workerLabel}
              color={workerNeedsAttention ? "error" : workerHealth?.state === "online" ? "success" : workerHealth?.state === "offline" ? "warning" : "default"}
              variant="outlined"
            />
            <Button
              aria-label="Refresh content reviews"
              onClick={() => void load()}
              disabled={loading}
              sx={{ minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET, px: { xs: 1, sm: 2 } }}
            >
              <RefreshIcon />
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, ml: 0.75 }}>
                Refresh
              </Box>
            </Button>
          </Stack>
        }
        sx={{ mb: 2, flexWrap: "wrap", gap: 1.5 }}
      />}

      <Box
        role="tablist"
        aria-label="Content review tabs"
        sx={{
          display: "flex",
          gap: mobile ? 0.75 : 1,
          overflowX: "auto",
          pb: mobile ? 0 : 1,
          mb: { xs: 1, sm: 2 },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          ...(mobile
            ? {
                // Floats over the reel, tucked under the blurred app bar.
                position: "fixed",
                top: "calc(env(safe-area-inset-top, 0px) + 64px)",
                left: 8,
                right: 8,
                zIndex: 1060,
                mb: 0,
              }
            : {}),
        }}
      >
        {TABS.map((entry) => (
          <Button
            key={entry.id}
            role="tab"
            variant={tab === entry.id ? "contained" : "outlined"}
            onClick={() => setTab(entry.id)}
            aria-selected={tab === entry.id}
            sx={{
              minHeight: mobile ? 32 : TOUCH_TARGET,
              whiteSpace: "nowrap",
              flexShrink: 0,
              ...(mobile
                ? {
                    py: 0,
                    px: 1.25,
                    fontSize: 12,
                    lineHeight: 1,
                    borderRadius: 999,
                    ...(tab === entry.id
                      ? { boxShadow: "none" }
                      : {
                          color: "rgba(255,255,255,0.82)",
                          borderColor: "rgba(255,255,255,0.22)",
                          bgcolor: "rgba(10,12,20,0.45)",
                          backdropFilter: "blur(12px)",
                        }),
                  }
                : {}),
            }}
          >
            {entry.label}
            {queue && (() => {
              const count = queue.counts[entry.id];
              return (
              <Box component="span" sx={{ ml: 0.75, opacity: 0.75 }}>
                {count === null || count === undefined ? "—" : count}
              </Box>
              );
            })()}
          </Button>
        ))}
        {mobile && <Button variant="contained" onClick={() => setSource("templates")} sx={{ flexShrink: 0, minHeight: 32, borderRadius: 999 }}>Templates</Button>}
        {mobile && healthWarning && (
          <Chip
            size="small"
            color="warning"
            label={healthWarning}
            sx={{ flexShrink: 0, height: 32, backdropFilter: "blur(12px)" }}
          />
        )}
      </Box>

      {tab === "approved" && (
        /* PKT-0612 — the DoD CONTENT instrument, on the screen it moves from.
           Desktop only: the mobile reel has no chrome budget for it. */
        !mobile && (
          <Typography
            variant="body2"
            color={postedSummary?.status === "ok" ? "text.secondary" : "warning.main"}
            data-testid="content-posted-headline"
            sx={{ mb: 1.5 }}
          >
            {postedHeadline}
          </Typography>
        )
      )}

      {mobile && queue && queue.items.length > 0 && (
        <Box
          aria-label="Reel position"
          sx={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 0px) + 108px)",
            left: 12,
            zIndex: 1060,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            bgcolor: "rgba(10,12,20,0.45)",
            backdropFilter: "blur(12px)",
            color: "rgba(255,255,255,0.8)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.3,
            pointerEvents: "none",
          }}
        >
          {Math.min(activeIndex + 1, queue.items.length)} / {queue.items.length}
        </Box>
      )}

      {error && !mobile && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Ch76 · P2 REPETITION STRIP — per beat_role, across the loaded queue:
          distinct/total captions + the most-repeated line. Computed client-side
          from scenes[] the page already holds; the point is "the hook is the
          same sentence on 9 of 12 reels", which no per-card read can show.
          Desktop Review tab only; silent when no repetition exists. */}
      {!mobile && tab === "review" && queue && (() => {
        const rows = beatRepetition(queue.items.map((i) => i.scenes)).filter(
          (r) => r.mostRepeated !== null,
        );
        if (rows.length === 0) return null;
        return (
          <Box
            data-testid="beat-repetition-strip"
            sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}
          >
            {rows.map((r) => (
              <Box
                key={r.beat_role}
                data-testid={`beat-repetition-${r.beat_role}`}
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  fontSize: 12,
                  color: "text.secondary",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {r.beat_role}
                </Box>
                {` · ${r.distinct} distinct of ${r.total} · most repeated (${r.mostRepeated!.count}×): “${r.mostRepeated!.caption}”`}
              </Box>
            ))}
          </Box>
        );
      })()}

      {mobile && error && !queue && !loading ? (
        <Box
          sx={{
            ...{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, height: feedHeight, width: "100vw", zIndex: 1050, bgcolor: "#05070D" },
            display: "grid",
            placeItems: "center",
            px: 4,
            textAlign: "center",
            color: "rgba(255,255,255,0.72)",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
              Couldn’t load the review queue
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
              Your queue was not changed. Check the connection and try again.
            </Typography>
            <Button
              variant="contained"
              onClick={() => void load()}
              sx={{ minHeight: TOUCH_TARGET, borderRadius: 999 }}
            >
              Try again
            </Button>
          </Box>
        </Box>
      ) : loading && !queue ? (
        <Box
          sx={mobile
            ? { ...{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, height: feedHeight, width: "100vw", zIndex: 1050, bgcolor: "#05070D" }, display: "grid", placeItems: "center" }
            : { minHeight: 360, display: "grid", placeItems: "center" }}
        >
          <CircularProgress aria-label="Loading content reviews" />
        </Box>
      ) : queue && queue.items.length === 0 ? (
        // 🔑 THE MODAL FIRST-DAY STATE. Nine packages sit in Review and both
        // other tabs are empty. An empty Approved tab is normal, not broken,
        // and every empty state below says what fills it.
        mobile ? (
          <Box
            aria-label={`${tabConfig.label} empty`}
            sx={{
              ...{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, height: feedHeight, width: "100vw", zIndex: 1050, bgcolor: "#05070D" },
              display: "grid",
              placeItems: "center",
              px: 4,
              textAlign: "center",
              color: "rgba(255,255,255,0.72)",
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                {tabConfig.emptyTitle}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                {tabConfig.emptyBody}
              </Typography>
            </Box>
          </Box>
        ) : (
        <EmptyState
          icon={<ContentIcon />}
          title={tabConfig.emptyTitle}
          description={tabConfig.emptyBody}
        />
        )
      ) : (
        <Box
          ref={feedRef}
          aria-label={mobile ? "Swipe review feed" : "Review wall"}
          sx={
            mobile
              ? {
                  ...{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, height: feedHeight, width: "100vw", zIndex: 1050, bgcolor: "#05070D" },
                  overflowY: "auto",
                  scrollSnapType: "y mandatory",
                  overscrollBehaviorY: "contain",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }
              : {
                  display: "grid",
                  gridTemplateColumns: {
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                    xl: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                  alignItems: "start",
                }
          }
        >
          {queue?.items.map((item, index) => (
            <ContentReviewCard
              key={item.id}
              item={item}
              mobile={mobile}
              active={mobile && index === activeIndex}
              busy={busyId === item.id || pendingFootage.some(saved => saved.packageId === item.id) || (manualFootageEnabled() && footageJournalError)}
              downloading={downloadingId === item.id}
              onApprove={approve}
              onReject={setRejectTarget}
              onSendBack={(item) => { setSelectedBeat(null); setFootageSeek(undefined); setSendBackTarget(item); }}
              onDownload={download}
              onMarkPosted={setPostedTarget}
              unknownVerdict={unknownById.has(item.id)
                ? pendingDecisions.current.get(item.id)?.verdict ?? null
                : null}
              onRetry={retry}
            />
          ))}
        </Box>
      )}

      <Stack sx={mobile?{position:"fixed",top:"calc(env(safe-area-inset-top, 0px) + 64px)",left:8,right:8,zIndex:1100,maxHeight:"30vh",overflow:"auto"}:{my:1}}>{pendingFootage.map(saved => <Alert key={saved.packageId} severity="warning" sx={{my:1}}>
        {saved.confirmedState === "applied"
          ? `The footage request for ${saved.packageId} was applied. Waiting for the new video to reach Review.`
          : saved.confirmedState ? `The footage request for ${saved.packageId} is ${saved.confirmedState}. Waiting for the Content engine.`
          : `The latest footage request status for ${saved.packageId} has not been confirmed in this review list.`} Other review actions for this reel are paused.
        <Button onClick={() => {
          const item=queue?.items.find(row=>row.id===saved.packageId);
          if(item){setSelectedBeat(null);setFootageSeek(undefined);setSendBackTarget(item);}
          else setRecoveringFootage(saved);
        }}>Check saved footage request</Button>
        {!manualFootageEnabled()?" Footage replacement is currently disabled; its request identity is retained.":null}
      </Alert>)}</Stack>
      {manualFootageEnabled() && footageJournalError?<Alert severity="warning">Saved footage request identities could not be read. Review actions are paused to avoid duplicate requests.</Alert>:null}

      {/* THE REVIEW SHEET — the ONLY place a note exists, and it cannot be
          submitted without a revision dial. No approve control lives here. */}
      <Drawer
        anchor="bottom"
        open={Boolean(sendBackTarget)}
        onClose={() => !busyId && closeSheets()}
        PaperProps={{ sx: SHEET_PAPER }}
      >
        <Typography variant="h5" fontWeight={800}>What should change?</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {sendBackTarget?.subject}. Pick what to fix and say it in your own words
          {sheetRevisionUnavailable
            ? "."
            : " — the engine re-renders it and sends it back to Review."}
        </Typography>
        {/*
          PKT-0601 — WATCH IT WHERE YOU REVIEW IT. Grant: "if you click review
          the video should pop up too there to be able to watch & review."

          It sits ABOVE the dials rather than beside them: this sheet is a
          560px-wide bottom drawer, so the packet's original "player left,
          review controls right" describes a wide dialog that does not exist
          here. Stacked, the reel reads at full width on a phone and the dials
          stay under the thumb.
        */}
        <ReviewSheetVideo item={sendBackTarget}
          seekTo={footageSeek}
          selectedSceneId={selectedBeat && selectedBeat.packageId === sendBackTarget?.id ? selectedBeat.sceneId : null}
          disabled={Boolean(busyId) || footageLocked}
          onSelectScene={sendBackTarget?.fingerprint && /^[a-f0-9]{64}$/.test(sendBackTarget.fingerprint)
            ? (scene) => setSelectedBeat(shotCopyTarget(sendBackTarget.id, scene, sendBackTarget.fingerprint!))
            : undefined}
        />
        {selectedBeat && selectedBeat.packageId === sendBackTarget?.id && (
          <Box sx={{mb: 2}}>
            <Typography variant="body2">Selected beat: {selectedBeat.sceneId} · clip {selectedBeat.clipId}</Typography>
            <Button disabled={Boolean(busyId) || footageLocked || !sendBackTarget.revision_capable}
              onClick={() => setEditingBeat(selectedBeat)}>Edit this beat’s words</Button>
          </Box>
        )}
        {sendBackTarget && <ManualFootageControl key={`${sendBackTarget.id}:${sendBackTarget.fingerprint}`}
          item={sendBackTarget} owner={owner} blocked={Boolean(busyId) || footageJournalError}
          onSeek={offsetSec=>setFootageSeek(previous=>({offsetSec,sequence:(previous?.sequence??0)+1}))}
          onLock={setFootageLocked}
          onRefresh={()=>{closeSheets();void load(true);}} onPending={trackFootage}
          onConfirmed={confirmFootage}
        />}
        {/*
          🔴 PKT-0427 — SAY WHAT WILL AND WILL NOT HAPPEN, ABOVE THE FIELD.

          🔴 DO NOT ADD "…and the fix lands when re-rendering is available."
          THE PROHIBITION STANDS. THE REASON BEHIND IT HAS CHANGED — read this
          before you decide it is safe to soften.

          It used to read: "The box NEVER reads this note: migration 055's lease
          returns an explicit column list that excludes `review_note`." 🔄 That
          is FALSE as of migration 085 (PKT-L5), which appends `review_note` to
          `lease_content_review_commands` at column 14 and is APPLIED in
          production — verified 2026-08-29 by `pg_get_functiondef` on the live
          database, and the running box release maps `command.review_note` off
          the lease.

          So why keep the prohibition? Because this Alert renders ONLY when
          `sheetRevisionUnavailable` — i.e. `revision_capable` is false — and
          for those packages `reviewCockpit` throws `NON-REVISABLE` on any K-*
          verdict. The note is leased, and the command is refused anyway. There
          is still no future re-render for THIS reel to consult it, so the
          promise would still be the 2026-07-25 lie.

          ⚠️ The copy below is therefore CORRECT AS WRITTEN and was deliberately
          left alone. It is scoped to the non-revisable case, where it is true.
          Do not "fix" it by reading only this header.
        */}
        {sheetRevisionUnavailable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Your note is saved. Re-rendering isn’t available for this reel yet.</strong>
            {sendBackTarget?.capability_reason
              ? ` ${sendBackTarget.capability_reason}`
              : ""}
            {" "}Nothing will come back to Review for this one. Your words are kept on this
            reel’s record as your review — the engine can’t act on them.
          </Alert>
        )}
        <Stack spacing={1}>
          {/*
            Ch72 · CHIPS, NOT STACKED BUTTONS.

            PROOF-0512 measured that the engine out-diagnoses the operator's dial
            choice, so the dial was ceremony weighted like a decision. Five
            stacked 58px buttons carrying two lines each pushed the NOTE FIELD —
            the thing the box now actually reads, since migration 085 — most of a
            phone screen down. Chips wrap, and the help for the chosen one lands
            under them.

            🔑 `component="button"` IS LOAD-BEARING, NOT STYLING. A default MUI
            Chip renders a div with role="button", which has no native `disabled`
            and cannot be focus-skipped. The fenced chip below MUST be genuinely
            disabled rather than merely styled that way, so these are real
            buttons.
          */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {REVISION_DIALS.map((entry) => (
              <Chip
                key={entry.verdict}
                component="button"
                type="button"
                clickable
                label={entry.label}
                disabled={Boolean(busyId) || footageLocked}
                aria-pressed={dial === entry.verdict}
                color={dial === entry.verdict ? "primary" : "default"}
                variant={dial === entry.verdict ? "filled" : "outlined"}
                onClick={() => setDial(entry.verdict)}
                sx={{ minHeight: TOUCH_TARGET, px: 0.5, fontWeight: 600 }}
              />
            ))}
            {/*
              🔴 THE FENCED CHIP. Rendered, greyed, and NOT a toggle — no
              aria-pressed, because it has no pressed state to report.

              It is here rather than absent because it is the verdict the engine
              STEERS TO when copy is claim-locked, and the one LN Pro cannot
              send: reviewCockpit.ts:730-736 demands failed_scene_id AND
              failed_clip_id AND q_codes unconditionally. PKT-0602 makes those
              carriable end to end (migration 103 + the route's anchor fields,
              gated OFF by CONTENT_REVIEW_ANCHORS_ENABLED until 103 is applied),
              but K-FOOT itself is still not an accepted verdict on the route or
              in the database, so the chip stays fenced until that is ruled.
              Grant filed footage asks as K-COPY twice on 2026-08-29 — because
              that is what the sheet offered — and both came back refused.
              Hiding the chip leaves him in that loop with no explanation.
            */}
            <Chip
              component="button"
              type="button"
              clickable
              label={FENCED_DIAL.label}
              disabled
              variant="outlined"
              sx={{ minHeight: TOUCH_TARGET, px: 0.5, fontWeight: 600 }}
            />
          </Box>
          {/*
            PKT-0427's promise, preserved through the redesign. The per-chip help
            no longer sits under every chip; it renders for the SELECTED one, and
            uses `helpNoteOnly` when the reel cannot be re-rendered so it still
            promises nothing there. The unrevisable Alert above is unchanged and
            is still legible BEFORE anything is typed — which was the binding
            requirement. Density changed; honesty did not.
          */}
          <Typography variant="caption" color="text.secondary" sx={{ minHeight: 20 }}>
            {dial
              ? (sheetRevisionUnavailable
                  ? REVISION_DIALS.find((entry) => entry.verdict === dial)?.helpNoteOnly
                  : REVISION_DIALS.find((entry) => entry.verdict === dial)?.help)
              : "Pick what to fix."}
          </Typography>
          {/*
            Mobile has no hover, so this cannot be a tooltip on the greyed chip.
            It has to be text, and it is worth the line: it names the one thing
            that has actually been costing Grant verdicts.
          */}
          <Typography variant="caption" color="text.secondary" data-testid="content-fenced-dial-note">
            {FENCED_DIAL.fencedBecause}
          </Typography>
          <TextField
            label="What's wrong? (optional)"
            placeholder="Say it how you'd say it out loud."
            value={note}
            onChange={(event) => setNote(event.target.value.slice(0, REVIEW_NOTE_MAX))}
            multiline
            minRows={2}
            fullWidth
            disabled={Boolean(busyId) || footageLocked}
            inputProps={{ maxLength: REVIEW_NOTE_MAX, "aria-label": "Re-render note" }}
          />
          <Button
            variant="contained"
            color="warning"
            disabled={Boolean(busyId) || footageLocked || !dial}
            onClick={() => sendBackTarget && dial && sendBack(sendBackTarget, dial, note)}
            sx={{ minHeight: TOUCH_TARGET }}
          >
            {sheetRevisionUnavailable ? "Save my review" : "Send back for a re-render"}
          </Button>
          <Button onClick={closeSheets} sx={{ minHeight: TOUCH_TARGET }}>Cancel</Button>
        </Stack>
      </Drawer>

      <Dialog open={Boolean(recoveringFootage)} onClose={()=>setRecoveringFootage(null)} fullWidth maxWidth="sm">
        <DialogTitle>Check saved footage request</DialogTitle>
        <DialogContent>
          {recoveringFootage && <ManualFootageControl key={recoveringFootage.packageId}
            item={{id:recoveringFootage.packageId,fingerprint:recoveringFootage.request.fingerprint,
              revision_capable:false,status:"unknown",verdict:"pending",command:null}}
            owner={owner} blocked={footageJournalError} onSeek={()=>{}} onLock={()=>{}}
            onRefresh={()=>{closeSheets();void load(true);}} onPending={trackFootage}
            onConfirmed={confirmFootage}/>}
          {!manualFootageEnabled()?<Typography>Footage replacement is currently disabled. The saved request identity is retained.</Typography>:null}
        </DialogContent>
        <DialogActions><Button onClick={()=>setRecoveringFootage(null)}>Close</Button></DialogActions>
      </Dialog>

      <ShotCopyDialog packageId={editingBeat?.packageId ?? null} target={editingBeat ?? undefined}
        onCancel={() => setEditingBeat(null)}
        onQueued={(request) => {
          if (!editingBeat) return;
          const command: ContentCommandTruth = {request_id: request.request_id, verdict: "K-COPY", state: "queued", applied: false};
          acceptedCopies.current.set(editingBeat.packageId, {command, fingerprint: editingBeat.fingerprint, previousRequestId: sendBackTarget?.command?.request_id ?? null});
          updateCommand(editingBeat.packageId, command);
          closeSheets();
          setNotice("Exact words requested for this beat. Review the new video when it returns.");
          void load(true);
        }}
      />

      {/* THE ✗ CONFIRM — one bit of information, yes or no. It takes no note
          and has nowhere to put one; see ContentRejectConfirm.tsx. */}
      <ContentRejectConfirm
        item={rejectTarget}
        busy={Boolean(busyId)}
        onConfirm={confirmReject}
        onCancel={closeSheets}
      />

      {/* PKT-0612 — record a manual post. Posting is manual by ruling; the
          sheet records the act and cannot publish. */}
      <ContentMarkPostedDialog
        item={postedTarget}
        busy={Boolean(recordingPostId)}
        onConfirm={(item, input) => void recordPost(item, input)}
        onDownload={download}
        onCancel={closeSheets}
      />

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={2800}
        onClose={() => setNotice(null)}
        message={notice}
      />
    </Box>
  );
};

/** The authenticated App identity, never a stored user label, chooses the journal. */
const ContentPage: React.FC<{userId?:string}> = ({userId}) => {
  const [revision,setRevision]=React.useState(0);
  React.useLayoutEffect(()=>clientStore.subscribe(key=>{
    if(key.startsWith("ln:persistence:"))setRevision(value=>value+1);
  }),[]);
  const owner=React.useMemo(()=>getManualFootageOwner(userId),[userId,revision]);
  return <ContentPageView key={JSON.stringify([userId,owner])} owner={owner}/>;
};
export default ContentPage;
